import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";
import { LucideIcon } from "lucide-react";
import { VideoPlayer } from "../VideoPlayer";
import { ReactVideoPlayer } from "../ReactVideoPlayer";
import { FontWrapper } from "../common/FontWrapper";
import { EditableText } from "../common/EditableText";
import { EditModeProvider } from "../../contexts/EditModeContext";
import { EditModePanel } from "../common/EditModePanel";
import { ScientificBasisInfo } from "../common/ScientificBasisInfo";
import { useIsMobile } from "../ui/use-mobile";
import { CallToAction } from "../ui/CallToAction";
import { deepMerge } from "../../utils/deepMerge";
import { useEditMode } from "../../contexts/EditModeContext";
import { CommentPinsOverlay } from "../ui/comment-pins-overlay";
import { DecorativeSceneIcon } from "../../presentation/DecorativeSceneIcon";
import { sceneMainClassName, sceneMediaClassName, sceneSubtitleClassName, sceneTitleClassName, useCardSceneLayout } from "../../presentation/cardSceneLayout";

export interface TranscriptRow {
  start: number;
  text: string;
}

// Utility function to check if video source is YouTube
const isYouTubeVideo = (src: string | undefined | null): boolean => {
  if (!src || typeof src !== 'string') return false;
  const lowerSrc = src.toLowerCase();
  return lowerSrc.includes('youtube') || lowerSrc.includes('youtu.be');
};

// İkon mapping fonksiyonu (memoized for performance)
const iconCache = new Map<string, LucideIcon>();

const getIconComponent = (iconName: string): LucideIcon => {
  if (iconCache.has(iconName)) {
    return iconCache.get(iconName)!;
  }

  // İkon adını camelCase'e çevir (örn: "book-open" -> "BookOpen")
  const camelCaseName = iconName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');

  // Lucide ikonlarını kontrol et
  let iconComponent: LucideIcon;
  if (camelCaseName in LucideIcons) {
    iconComponent = LucideIcons[camelCaseName as keyof typeof LucideIcons] as LucideIcon;
  } else {
    // Fallback ikon
    console.warn(`Icon "${iconName}" not found, using default icon`);
    iconComponent = LucideIcons.HelpCircle;
  }

  iconCache.set(iconName, iconComponent);
  return iconComponent;
};

// Default values moved outside component to prevent recreation
const DEFAULT_CONTAINER_CLASS = "flex flex-col items-center justify-start h-full sm:px-6 overflow-y-auto";
const DEFAULT_VIDEO_CONTAINER_CLASS = "w-full max-w-sm sm:max-w-md lg:max-w-lg";

// Parse text that contains inline timestamps (e.g., "00:00:05 Text 00:00:08 More text...")
function parseInlineTimestampedText(text: string): TranscriptRow[] {
  if (!text || typeof text !== "string") return [];

  const rows: TranscriptRow[] = [];
  // Match both formats: 00:00:05 and 00:00:05.000
  const regex = /(\d{2}):(\d{2}):(\d{2})(?:\.(\d{3}))?/g;

  let lastIndex = 0;
  let currentStart = 0;
  let match: RegExpExecArray | null;

  const pushSegment = (segment: string, start: number) => {
    const cleaned = segment
      .replace(/\s+/g, " ")
      .replace(/^\s+|\s+$/g, "");
    if (cleaned.length > 0) {
      rows.push({ start, text: cleaned });
    }
  };

  while ((match = regex.exec(text)) !== null) {
    const segment = text.slice(lastIndex, match.index);
    pushSegment(segment, currentStart);

    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const seconds = parseInt(match[3], 10);
    const milliseconds = match[4] ? parseInt(match[4], 10) : 0;
    currentStart = hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;

    lastIndex = match.index + match[0].length;
  }

  const tail = text.slice(lastIndex);
  pushSegment(tail, currentStart);

  return rows;
}

// Enhanced video data with multi-language transcript support
function parseTactiqTranscript(raw: string): TranscriptRow[] {
  if (!raw || typeof raw !== "string") {
    return [];
  }

  // First check if transcript has line breaks (traditional format)
  const hasLineBreaks = raw.includes('\n') || raw.includes('\\n');

  if (hasLineBreaks) {
    // Backend'den gelen format: "00:00:00 Text\n00:00:04 More text\n..."
    const lines = raw.split(/\\n|\n/); // Hem \n hem \\n destekle
    const transcript: TranscriptRow[] = [];

    for (const line of lines) {
      if (!line.trim()) continue;

      // Backend format: 00:00:04 Text content
      const backendMatch = line.match(/^(\d{2}):(\d{2}):(\d{2})\s+(.+)$/);

      // Önceki format: 00:00:02.000 [Music]
      const tactiqMatch = line.match(/^(\d{2}):(\d{2}):(\d{2})\.(\d{3})\s+(.+)$/);

      if (backendMatch) {
        const h = parseInt(backendMatch[1], 10);
        const m = parseInt(backendMatch[2], 10);
        const s = parseInt(backendMatch[3], 10);
        const start = h * 3600 + m * 60 + s;
        const text = backendMatch[4].trim();

        if (text) {
          transcript.push({ start, text });
        }
      } else if (tactiqMatch) {
        const h = parseInt(tactiqMatch[1], 10);
        const m = parseInt(tactiqMatch[2], 10);
        const s = parseInt(tactiqMatch[3], 10);
        const ms = parseInt(tactiqMatch[4], 10);
        const start = h * 3600 + m * 60 + s + ms / 1000;
        const text = tactiqMatch[5].trim();

        if (text) {
          transcript.push({ start, text });
        }
      }
    }

    console.log('Parsed transcript entries (line-by-line):', transcript.length);
    return transcript.sort((a, b) => a.start - b.start);
  } else {
    // No line breaks - treat as inline timestamped text
    // Format: "00:00:05 Text 00:00:08 More text..."
    const parsed = parseInlineTimestampedText(raw);
    console.log('Parsed transcript entries (inline timestamps):', parsed.length);
    return parsed.sort((a, b) => a.start - b.start);
  }
}

// URL'den transcript yükleme fonksiyonu
async function fetchTranscriptFromUrl(url: string): Promise<string> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.text();
  } catch (error) {
    console.error('Error fetching transcript from URL:', error);
    throw error;
  }
}

interface ScenarioSceneConfig {
  title: string;
  subtitle: string;
  callToActionText?: string | { mobile?: string; desktop?: string; };
  description: string;
  video: {
    src: string;
    poster?: string;
    disableForwardSeek?: boolean;
    showTranscript?: boolean;
    transcriptTitle?: string;
    transcriptLanguage?: string;
    transcript?: string;
    /**
     * WebVTT captions track for the video's own audio, so the player can offer a real CC ON/OFF
     * control. One track per scene: it follows the SPOKEN language of the video, which does not
     * change when the training is localized. Absent for stock library videos, whose subtitles
     * travel inside the HLS stream.
     */
    captions?: {
      src: string;
      language: string;
    } | null;
  };
  icon?: {
    component?: React.ReactNode;
    size?: number;
    sceneIconName?: string;
    className?: string;
  };
  containerClassName?: string;
  videoContainerClassName?: string;
  texts?: {
    transcriptLoading?: string;
    ctaLocked?: string;
    ctaUnlocked?: string;
  };
  ariaTexts?: {
    mainLabel?: string;
    mainDescription?: string;
    loadingLabel?: string;
    errorLabel?: string;
    videoPlayerLabel?: string;
  };
}
interface ScenarioSceneProps {
  config: ScenarioSceneConfig;
}

export function ScenarioScene({
  config,
  appConfig,
  onNextSlide,
  onVideoCompleted,
  sceneId,
  reducedMotion,
  disableDelays,
  isVideoCompleted: controlledIsVideoCompleted,
  onIsVideoCompletedChange
}: ScenarioSceneProps & {
  appConfig?: any;
  onNextSlide?: () => void;
  onVideoCompleted?: (completed: boolean) => void;
  sceneId?: string | number;
  reducedMotion?: boolean;
  disableDelays?: boolean;
  isVideoCompleted?: boolean;
  onIsVideoCompletedChange?: (completed: boolean) => void;
}) {

  // Edit mode is handled by EditModeProvider inside ScenarioSceneContent

  // State for edit changes and edit mode tracking
  const [editChanges, setEditChanges] = useState<Partial<ScenarioSceneConfig>>({});
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [configKey, setConfigKey] = useState(0);

  // Use isInEditMode state instead of nested useEditMode hook
  const currentEditMode = isInEditMode;

  // Detect language changes and force re-render
  useEffect(() => {
    setConfigKey(prev => prev + 1);
    setEditChanges({}); // Clear edit changes on language switch
  }, [config.title, config.subtitle]); // Use specific fields to detect language change

  // Get tempConfig from EditModeProvider (we're inside EditModeProvider)
  // We'll use this in the internal component that's rendered inside EditModeProvider
  // For now, keep using editChanges for backward compatibility
  const currentConfig = useMemo(() => {
    const merged = deepMerge(config, editChanges);
    console.log('ScenarioScene currentConfig updated - config:', config);
    console.log('ScenarioScene currentConfig updated - editChanges:', editChanges);
    console.log('ScenarioScene currentConfig updated - merged:', merged);
    return merged;
  }, [config, editChanges]);

  // Clear edit changes when exiting edit mode
  useEffect(() => {
    if (!isInEditMode) {
      setEditChanges({});
    }
  }, [isInEditMode]);

  const [transcriptData, setTranscriptData] = useState<string>('');
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);
  const [transcriptError, setTranscriptError] = useState<string | null>(null);
  // Use controlled props if provided, otherwise fallback to local state
  const [localIsVideoCompleted, setLocalIsVideoCompleted] = useState(false);

  const isVideoCompleted = controlledIsVideoCompleted !== undefined ? controlledIsVideoCompleted : localIsVideoCompleted;

  const setIsVideoCompleted = (value: boolean) => {
    if (onIsVideoCompletedChange) onIsVideoCompletedChange(value);
    else setLocalIsVideoCompleted(value);
  };



  // Notify parent when video completion status changes
  useEffect(() => {
    onVideoCompleted?.(isVideoCompleted);
  }, [isVideoCompleted, onVideoCompleted]);

  // URL algılama fonksiyonu
  const isUrl = (str: string): boolean => {
    try {
      // URL pattern'lerini kontrol et
      const urlPatterns = [
        /^https?:\/\//,           // http:// veya https://
        /^\/\//,                  // // (protocol-relative)
        /^\/[^/]/,               // /path (absolute path)
        /^\.\/|\/\./,             // ./path veya /./path (relative path)
        /^[a-zA-Z0-9-]+:\/\//,    // custom:// (custom protocols)
      ];

      return urlPatterns.some(pattern => pattern.test(str.trim()));
    } catch {
      return false;
    }
  };

  // Transcript'i dinamik olarak yükle (string veya URL'den)
  useEffect(() => {
    const loadTranscript = async () => {
      // Transcript boş veya undefined ise state'i temizle
      if (!currentConfig.video?.transcript || currentConfig.video.transcript === '') {
        setTranscriptData('');
        setIsLoadingTranscript(false);
        setTranscriptError(null);
        return;
      }

      const transcriptValue = currentConfig.video.transcript;

      // Transcript'in tipini dinamik olarak algıla
      if (typeof transcriptValue === 'string') {
        if (isUrl(transcriptValue)) {
          // URL olarak algılandı - fetch et
          setIsLoadingTranscript(true);
          setTranscriptError(null);

          try {
            const transcriptText = await fetchTranscriptFromUrl(transcriptValue);
            setTranscriptData(transcriptText);
          } catch (error) {
            setTranscriptError(`Failed to load transcript: ${error instanceof Error ? error.message : 'Unknown error'}`);
          } finally {
            setIsLoadingTranscript(false);
          }
        } else {
          // String olarak algılandı - direkt kullan
          setTranscriptData(transcriptValue);
        }
      } else {
        // Diğer tipler için string'e çevir
        console.log('🔄 Transcript string\'e çevriliyor');
        setTranscriptData(String(transcriptValue));
      }
    };

    loadTranscript();
  }, [currentConfig.video?.transcript]);

  // Parse transcript from loaded data
  const tactiqTranscript = useMemo(
    () => parseTactiqTranscript(transcriptData),
    [transcriptData]
  );

  // Memoize icon component and other values together (performance optimized)
  const memoizedValues = useMemo(() => {
    // Icon component
    let iconComponent;
    if (currentConfig.icon?.component) {
      iconComponent = currentConfig.icon.component;
    } else {
      const SceneIcon = getIconComponent(currentConfig.icon?.sceneIconName || 'play-circle');
      iconComponent = (
        <SceneIcon
          size={currentConfig.icon?.size || 40}
          className={`text-[#1C1C1E] dark:text-[#F2F2F7]`}
          aria-hidden="true"
        />
      );
    }

    // Container classes
    const containerClassName = currentConfig.containerClassName || DEFAULT_CONTAINER_CLASS;
    const videoContainerClassName = currentConfig.videoContainerClassName || DEFAULT_VIDEO_CONTAINER_CLASS;

    return {
      iconComponent,
      containerClassName,
      videoContainerClassName
    };
  }, [
    currentConfig.icon?.component,
    currentConfig.icon?.sceneIconName,
    currentConfig.icon?.size,
    currentConfig.containerClassName,
    currentConfig.videoContainerClassName
  ]);

  const isMobile = useIsMobile();

  // Memoize callbacks for better performance
  const handleSave = useCallback((newConfig: any) => {
    console.log('ScenarioScene handleSave - newConfig:', newConfig);
    console.log('ScenarioScene handleSave - original config:', config);
    setEditChanges(newConfig);
    console.log('ScenarioScene handleSave - editChanges set');
  }, [config]);

  return (
    <EditModeProvider
      key={configKey}
      initialConfig={currentConfig}
      sceneId={sceneId?.toString()}
      onSave={handleSave}
      onEditModeChange={setIsInEditMode}
    >
      <ScenarioSceneContent
        config={config}
        editChanges={editChanges}
        appConfig={appConfig}
        onNextSlide={onNextSlide}
        onVideoCompleted={onVideoCompleted}
        sceneId={sceneId}
        reducedMotion={reducedMotion}
        disableDelays={disableDelays}
        isVideoCompleted={isVideoCompleted}
        setIsVideoCompleted={setIsVideoCompleted}
        currentEditMode={currentEditMode}
        isMobile={isMobile}
        memoizedValues={memoizedValues}
        tactiqTranscript={tactiqTranscript}
        transcriptData={transcriptData}
        isLoadingTranscript={isLoadingTranscript}
        transcriptError={transcriptError}
      />
    </EditModeProvider>
  );
}

// Internal component that uses EditModeProvider's tempConfig
function ScenarioSceneContent({
  config,
  editChanges,
  appConfig,
  onNextSlide,
  onVideoCompleted,
  sceneId,
  reducedMotion,
  disableDelays,
  isVideoCompleted,
  setIsVideoCompleted,
  currentEditMode,
  isMobile,
  memoizedValues,
  tactiqTranscript,
  transcriptData,
  isLoadingTranscript,
  transcriptError,
}: {
  config: ScenarioSceneConfig;
  editChanges: Partial<ScenarioSceneConfig>;
  appConfig?: any;
  onNextSlide?: () => void;
  onVideoCompleted?: (completed: boolean) => void;
  sceneId?: string | number;
  reducedMotion?: boolean;
  disableDelays?: boolean;
  isVideoCompleted: boolean;
  setIsVideoCompleted: (completed: boolean) => void;
  currentEditMode: boolean;
  isMobile: boolean;
  memoizedValues: any;
  tactiqTranscript: TranscriptRow[];
  transcriptData: string;
  isLoadingTranscript: boolean;
  transcriptError: string | null;
}) {
  // Get tempConfig from EditModeProvider
  const { tempConfig, isEditMode } = useEditMode();
  const layout = useCardSceneLayout();

  // Compute current config using tempConfig from EditModeProvider
  const currentConfig = useMemo(() => {
    // Merge config with editChanges first, then with tempConfig from EditModeProvider
    const mergedWithEditChanges = deepMerge(config, editChanges);
    const mergedWithTempConfig = deepMerge(mergedWithEditChanges, tempConfig || {});
    console.log('ScenarioSceneContent currentConfig updated - config:', config);
    console.log('ScenarioSceneContent currentConfig updated - editChanges:', editChanges);
    console.log('ScenarioSceneContent currentConfig updated - tempConfig:', tempConfig);
    console.log('ScenarioSceneContent currentConfig updated - merged:', mergedWithTempConfig);
    return mergedWithTempConfig;
  }, [config, editChanges, tempConfig]);

  return (
    <>
      <EditModePanel sceneId={sceneId} sceneLabel={(currentConfig as any)?.title} appConfig={appConfig} />
      <ScientificBasisInfo
        config={currentConfig}
        sceneType={(currentConfig as any)?.scene_type || 'scenario'}
      />
      <FontWrapper>
        <main
          className={sceneMainClassName(memoizedValues.containerClassName, layout, "relative")}
          role="main"
          aria-labelledby="scenario-scene-title"
          aria-describedby="scenario-scene-description"
          aria-label={currentConfig.ariaTexts?.mainLabel || "Scenario Scene"}
          data-scene-type={currentConfig as any ? (currentConfig as any).scene_type || 'scenario' : 'scenario'}
          data-scene-id={sceneId as any}
          data-testid="scene-scenario"
          data-comment-surface="true"
        >
          {/* Header Icon */}
          {!isMobile && (
            <DecorativeSceneIcon>
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: -20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: reducedMotion ? 0 : 0.8, ease: "easeOut" }}
                className={`mb-1 sm:mb-2 p-3 ${currentEditMode ? 'glass-border-3-no-overflow' : 'glass-border-3'}`}
                aria-hidden="true"
              >
                {memoizedValues.iconComponent}
              </motion.div>
            </DecorativeSceneIcon>
          )}

          {/* Title */}
          <motion.h1
            id="scenario-scene-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reducedMotion ? 0 : 0.8, delay: reducedMotion ? 0 : 0.2 }}
            className={sceneTitleClassName(layout)}
          >
            <EditableText
              configPath="title"
              placeholder="Enter scenario title..."
              maxLength={100}
              as="span"
            >
              {currentConfig.title}
            </EditableText>
          </motion.h1>
          {/* Subtitle */}
          {currentConfig.subtitle && (
            <motion.div
              id="scenario-scene-subtitle"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: reducedMotion ? 0 : 0.6, delay: reducedMotion ? 0 : 0.3 }}
              className={sceneSubtitleClassName(layout)}
              aria-label="Subtitle"
            >
              <EditableText
                configPath="subtitle"
                placeholder="Enter scenario subtitle..."
                maxLength={200}
                multiline={true}
                as="span"
              >
                {currentConfig.subtitle}
              </EditableText>
            </motion.div>
          )}

          {/* Description */}
          {currentConfig.description && (
            <motion.div
              id="scenario-scene-description"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: reducedMotion ? 0 : 0.8, delay: reducedMotion ? 0 : 0.3 }}
              className="text-base text-[#1C1C1E] dark:text-[#F2F2F7] text-center mb-4"
            >
              <EditableText
                configPath="description"
                placeholder="Enter scenario description..."
                maxLength={300}
                multiline={true}
                as="span"
              >
                {currentConfig.description}
              </EditableText>
            </motion.div>
          )}

          {/* Loading State */}
          {isLoadingTranscript && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-8"
              role="status"
              aria-live="polite"
              aria-label={currentConfig.ariaTexts?.loadingLabel || "Loading transcript"}
            >
              <div className="inline-flex items-center space-x-2">
                <div
                  className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"
                  aria-hidden="true"
                ></div>
                <span className="text-gray-600 dark:text-gray-400">
                  {currentConfig.texts?.transcriptLoading || "Loading transcript..."}
                </span>
              </div>
            </motion.div>
          )}

          {/* Error State */}
          {transcriptError && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-8"
              role="alert"
              aria-live="assertive"
              aria-label={currentConfig.ariaTexts?.errorLabel || "Transcript loading error"}
            >
              <div className="inline-flex items-center space-x-2 text-[#1C1C1E] dark:text-[#F2F2F7]">
                <span>{transcriptError}</span>
              </div>
            </motion.div>
          )}

          {/* Video Player */}
          {!isLoadingTranscript && !transcriptError && (
            <motion.section
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className={sceneMediaClassName(memoizedValues.videoContainerClassName, layout)}
              role="region"
              aria-label={currentConfig.ariaTexts?.videoPlayerLabel || "Video Player"}
              data-testid="scenario-video-section"
            >
              {isYouTubeVideo(currentConfig.video.src) ? (
                <ReactVideoPlayer
                  src={currentConfig.video.src}
                  width="512px"
                  height="288px"
                  controls={false}
                  playing={false}
                  disableForwardSeek={false}
                  className="w-full"
                  data-testid="scenario-video"
                  transcript={tactiqTranscript}
                  showTranscript={currentConfig.video.showTranscript}
                  transcriptTitle={currentConfig.video.transcriptTitle}
                  sceneId={sceneId}
                  onEnded={() => setIsVideoCompleted(true)}
                />
              ) : (
                <VideoPlayer
                  src={currentConfig.video.src}
                  poster={currentConfig.video.poster || undefined}
                  disableForwardSeek={currentConfig.video.disableForwardSeek}
                  transcript={tactiqTranscript}
                  showTranscript={currentConfig.video.showTranscript}
                  transcriptTitle={currentConfig.video.transcriptTitle}
                  captions={currentConfig.video.captions}
                  sceneId={sceneId}
                  // Without this the scene never learns the video finished, and `videoCompleted`
                  // gates BOTH the CTA and forward navigation for a `scenario` scene
                  // (navigationGuards.ts), so the learner is stuck on scene 3 forever. The YouTube
                  // branch above has always had it; this one lost it when the two were split.
                  onEnded={() => setIsVideoCompleted(true)}
                />
              )}
            </motion.section>
          )}

          {/* Call to Action */}
          {currentConfig.callToActionText && (
            <CallToAction
              text={isVideoCompleted
                ? (
                  typeof currentConfig.callToActionText === 'string'
                    ? currentConfig.callToActionText
                    : (typeof currentConfig.callToActionText === 'object' ?
                      (currentConfig.callToActionText.mobile) :
                      currentConfig.texts?.desktop || 'Continue')
                )
                : (currentConfig.texts?.ctaLocked || 'Watch to continue')}
              mobileText={typeof currentConfig.callToActionText === 'object' ? currentConfig.callToActionText.mobile : undefined}
              desktopText={typeof currentConfig.callToActionText === 'object' ? currentConfig.callToActionText.desktop : undefined}
              fieldLabels={{
                mobile: "After finished",
                desktop: "Unfinished"
              }}
              className="mt-0 sm:mt-0"
              delay={0.8}
              onClick={onNextSlide}
              dataTestId="cta-scenario"
              noCheckMobile={isVideoCompleted}
              disabled={!isVideoCompleted && !isEditMode}
            />
          )}

          <CommentPinsOverlay sceneId={sceneId} />
        </main>
      </FontWrapper>
    </>
  );
}